/**
 * Virtualised, flattened block tree (spec §8.6): rows are `{treeId, depth}`; heavy editors mount
 * lazily as rows enter the viewport (+overscan). Moves are native tree moves via dnd-kit.
 */
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragMoveEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, type SortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { LoroDoc, TreeID } from "loro-crdt";
import { ClipboardPaste, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { BlockType } from "@kss/schema";
import { blockIri } from "../lib/iri";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { usePopover } from "../ui/usePopover";
import { useT } from "../i18n";
import { clearHeight as clearBlockHeight, readHeight, setHeight as setBlockHeight } from "./height";
import { BlockChrome, BlockMenu } from "./BlockChrome";
import type { BlockIssue } from "../review/api";
import { blocksTree, createBlock, deleteBlock, flattenBlocks, moveBlock, moveBlockRelative, pageTitleText, type FlatBlock } from "./model";
import { dropPlacement, isSelfOrDescendant, type DropPlacement } from "./dropZones";
import { readRoles } from "../links/roles";
import { copyBlocks, countBlocks, useClipboard, type ClipboardEntry } from "./clipboard/store";
import { PasteDialog } from "./clipboard/PasteDialog";
import { resolveSource } from "./clipboard/sources";
import { mirrorSourceOf, pasteBlocks, resolveMirrors, snapBlock, type PasteTarget } from "./clipboard/snapshot";
import { availableBlockTypes, blockDef, blockRegistry } from "./registry";
import { usePageEditorContext } from "../pages/pageEditorContext";
import { layoutRail, type EdgePath } from "../rail/layout";
import { buildRailEdges } from "../rail/model";
import { RailOverlay, type OverlayEndpoints } from "../rail/RailOverlay";
import { rowGeometry, useBundleOptions, usePageRelations, useQueryTracks, useRailConfig, useRailStyles } from "../rail/useRail";

interface Props {
  handle: DocHandle;
  readOnly: boolean;
  userId: string;
  focusedBy: Map<string, Array<{ name: string; color: string }>>;
  onFocusBlock(treeId: TreeID | null): void;
  onLinkBlock?(treeId: TreeID, blockType: BlockType): void;
  /** [M18] "Set role…" in the block menu (spec §8.11) */
  onSetRole?(treeId: TreeID): void;
  sequences?: Array<{ id: string; label: string }>;
  onAddToSequence?(seqId: string, treeId: TreeID): void;
  workspaceId?: string;
  issues?: Map<string, BlockIssue[]>;
  /** the workspace document, for the relation rail's style palette (§7.5) */
  wsDoc?: LoroDoc | null;
  /**
   * Embedded in a mirror block (spec §8.18): no virtualiser, scroll container or rail; rows limited to
   * the subtree of `rootTreeId` (all blocks without it). Everything acts on this `handle`'s document.
   */
  embedded?: { rootTreeId?: TreeID; sourceLabel: string };
}

/** Rows of one subtree, with depths relative to its root. */
function subtreeRows(rows: FlatBlock[], rootTreeId: TreeID): FlatBlock[] {
  const start = rows.findIndex((r) => r.treeId === rootTreeId);
  if (start < 0) return [];
  const base = rows[start]!.depth;
  const out = [{ ...rows[start]!, depth: 0 }];
  for (let i = start + 1; i < rows.length && rows[i]!.depth > base; i++) out.push({ ...rows[i]!, depth: rows[i]!.depth - base });
  return out;
}

/** Every block's planning roles, for the pill (spec §8.6 item 6). */
function blockRoles(doc: LoroDoc, docId: string): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const row of flattenBlocks(doc)) {
    const roles = readRoles(doc, docId, row.treeId).map((r) => r.iri);
    if (roles.length) out.set(row.treeId, roles);
  }
  return out;
}

const sameRoleMap = (a: Map<string, string[]>, b: Map<string, string[]>): boolean =>
  a.size === b.size && [...a].every(([k, v]) => (b.get(k) ?? []).join() === v.join());

/** [M21] the user-set heights of §8.6 item 8, read in one pass so a resize re-renders the tree once */
function blockHeights(doc: LoroDoc): Map<string, number> {
  const tree = blocksTree(doc);
  const out = new Map<string, number>();
  for (const row of flattenBlocks(doc)) {
    const node = tree.getNodeByID(row.treeId);
    const h = node && readHeight(node);
    if (h) out.set(row.treeId, h);
  }
  return out;
}

const sameHeightMap = (a: Map<string, number>, b: Map<string, number>): boolean => a.size === b.size && [...a].every(([k, v]) => b.get(k) === v);

export function BlockTree({ handle, readOnly, userId, focusedBy, onFocusBlock, onLinkBlock, onSetRole, sequences, onAddToSequence, workspaceId, issues, wsDoc = null, embedded }: Props) {
  const t = useT();
  const flat = useLoroSelector(handle.doc, flattenBlocks, sameRows);
  // [M18] the role pill of §8.6 item 6: what each block says it is
  const roleIris = useLoroSelector(handle.doc, (d) => blockRoles(d, handle.crdtDocId), sameRoleMap) ?? new Map<string, string[]>();
  const heights = useLoroSelector(handle.doc, blockHeights, sameHeightMap) ?? new Map<string, number>();
  const rootTreeId = embedded?.rootTreeId;
  const allRows = useMemo(() => (flat && rootTreeId ? subtreeRows(flat, rootTreeId) : (flat ?? [])), [flat, rootTreeId]);
  // collapsed groups (UI-local): descendants of a collapsed group are hidden
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleCollapse = useCallback((id: string) => setCollapsed((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const rows = useMemo(() => {
    if (collapsed.size === 0) return allRows;
    const hidden = new Set<string>();
    return allRows.filter((r) => {
      if (r.parent && hidden.has(r.parent)) { hidden.add(r.treeId); return false; }
      if (r.type === "group" && collapsed.has(r.treeId)) hidden.add(r.treeId);
      return true;
    });
  }, [allRows, collapsed]);
  const parentRef = useRef<HTMLDivElement>(null);
  const ctx = usePageEditorContext();

  // block selection (spec §8.18): per tab, never in Loro
  const [selection, setSelection] = useState<Set<TreeID>>(() => new Set());
  const anchor = useRef<TreeID | null>(null);
  const clipboard = useClipboard();
  const canPaste = !readOnly && !!clipboard && !!workspaceId && clipboard.workspaceId === workspaceId;
  const [pasting, setPasting] = useState<{ entry: ClipboardEntry; target: PasteTarget; where: string } | null>(null);
  const selectRow = (row: FlatBlock, e: React.MouseEvent) => {
    if (e.shiftKey && anchor.current) {
      const a = rows.findIndex((r) => r.treeId === anchor.current);
      const b = rows.findIndex((r) => r.treeId === row.treeId);
      if (a >= 0 && b >= 0) {
        setSelection((cur) => new Set([...cur, ...rows.slice(Math.min(a, b), Math.max(a, b) + 1).map((r) => r.treeId)]));
        return;
      }
    }
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      anchor.current = row.treeId;
      setSelection((cur) => {
        const next = new Set(cur);
        if (next.has(row.treeId)) next.delete(row.treeId);
        else next.add(row.treeId);
        return next;
      });
    }
  };
  const [copied, setCopied] = useState<string | null>(null);
  const copy = useCallback(
    async (ids: Iterable<TreeID>) => {
      if (!workspaceId) return;
      const entry = await copyBlocks({ doc: handle.doc, docId: handle.crdtDocId, workspaceId, pageTitle: embedded?.sourceLabel ?? pageTitleText(handle.doc)?.toString() ?? "", ids });
      const n = countBlocks(entry.blocks);
      setCopied(t("block.copied", { count: n }));
      setTimeout(() => setCopied(null), 2500);
    },
    [handle, workspaceId, embedded?.sourceLabel, t],
  );
  const openPaste = useCallback(
    (target: Omit<PasteTarget, "docId">, where: string) => clipboard && setPasting({ entry: clipboard, target: { docId: handle.crdtDocId, ...target }, where }),
    [clipboard, handle.crdtDocId],
  );
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  useEffect(() => {
    if (embedded) return;
    const editable = (t: EventTarget | null) => t instanceof HTMLElement && !!t.closest("input, textarea, [contenteditable='true'], .cm-editor, [role='dialog']");
    const onKey = (e: KeyboardEvent) => {
      const sel = selectionRef.current;
      if (e.key === "Escape" && sel.size) setSelection(new Set());
      if (!(e.ctrlKey || e.metaKey) || !sel.size || editable(e.target)) return;
      if (e.key === "c") {
        e.preventDefault();
        void copy(sel);
      } else if (e.key === "v" && canPaste) {
        e.preventDefault();
        const last = [...allRows].reverse().find((r) => sel.has(r.treeId));
        if (last) openPaste({ parent: last.parent ?? undefined, index: last.index + 1 }, "paste.title.afterSelection");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [embedded, copy, canPaste, openPaste, allRows]);

  const detach = async (row: FlatBlock) => {
    const node = blocksTree(handle.doc).getNodeByID(row.treeId);
    const source = node ? mirrorSourceOf(snapBlock(handle.crdtDocId, node)) : null;
    const found = source ? await resolveSource(source) : null;
    const original = found && (await resolveMirrors(found, resolveSource));
    if (!original) {
      window.alert(t("block.mirror.unavailable"));
      return;
    }
    pasteBlocks(handle.doc, userId, { docId: handle.crdtDocId, parent: row.parent ?? undefined, index: row.index, replace: row.treeId }, "copy", original);
  };
  const virtualizer = useVirtualizer({
    count: embedded ? 0 : rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 64,
    overscan: 6,
    getItemKey: (i) => rows[i]!.treeId,
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const ids = useMemo(() => rows.map((r) => r.treeId), [rows]);

  // relation rail (spec §8.15): nothing renders — and the spacer keeps its exact style — unless the
  // page enables it and something is drawn
  const railConfig = useRailConfig(handle.doc);
  const railStyles = useRailStyles(wsDoc);
  const bundleOptions = useBundleOptions();
  const { relations, groups } = usePageRelations(handle);
  const { states: queryTracks } = useQueryTracks(workspaceId, handle.crdtDocId, railConfig, !embedded);
  const rail = useMemo(() => {
    if (!railConfig.enabled || embedded) return null;
    const queryResults = new Map([...queryTracks].map(([id, st]) => [id, st.edges]));
    const { edges, groups: layoutGroups } = buildRailEdges({ docId: handle.crdtDocId, config: railConfig, styles: railStyles, relations, groups, queryResults, allRows, visibleRows: rows });
    if (!edges.length) return null;
    const endpoints = new Map<string, OverlayEndpoints>(edges.map((e) => [e.id, { from: rows[e.from]!.treeId, to: rows[e.to]!.treeId, trackId: e.id.split(":")[0]! }]));
    return { edges, groups: layoutGroups, endpoints };
  }, [railConfig, railStyles, relations, groups, queryTracks, allRows, rows, handle.crdtDocId]);
  const railLayout = rail ? layoutRail(rowGeometry(virtualizer), rail.edges, rail.groups, bundleOptions) : null;
  const jump = (path: EdgePath) => {
    // scroll to whichever end is further from the middle of the viewport
    const edge = rail?.edges.find((e) => e.id === path.id);
    const el = parentRef.current;
    if (!edge || !el) return;
    const middle = el.scrollTop + el.clientHeight / 2;
    const target = Math.abs(path.y1 - middle) > Math.abs(path.y2 - middle) ? edge.from : edge.to;
    virtualizer.scrollToIndex(target, { align: "center" });
  };

  // drag and drop (spec §8.6 item 4): the pointer's position over the target row decides before / into /
  // after; rows do not shift while dragging, so the zones stay where the reader sees them
  const [dropHint, setDropHint] = useState<{ treeId: TreeID; place: DropPlacement } | null>(null);
  const altHeld = useRef(false);
  useEffect(() => {
    const key = (e: KeyboardEvent) => (altHeld.current = e.altKey);
    window.addEventListener("keydown", key);
    window.addEventListener("keyup", key);
    return () => {
      window.removeEventListener("keydown", key);
      window.removeEventListener("keyup", key);
    };
  }, []);
  const placementOf = (e: DragMoveEvent | DragEndEvent): { target: FlatBlock; place: DropPlacement } | null => {
    if (readOnly || !e.over || e.active.id === e.over.id) return null;
    const target = rows.find((r) => r.treeId === e.over!.id);
    if (!target || isSelfOrDescendant(handle.doc, e.active.id as TreeID, target.treeId)) return null;
    const start = e.activatorEvent as PointerEvent | MouseEvent;
    const y = (start?.clientY ?? 0) + e.delta.y;
    return { target, place: dropPlacement(y, e.over.rect, target.type === "group") };
  };
  const onDragMove = (e: DragMoveEvent) => {
    const p = placementOf(e);
    setDropHint((cur) => (p ? (cur?.treeId === p.target.treeId && cur.place === p.place ? cur : { treeId: p.target.treeId, place: p.place }) : null));
  };
  const onDragEnd = (e: DragEndEvent) => {
    setDropHint(null);
    const p = placementOf(e);
    if (!p) return;
    const active = e.active.id as TreeID;
    if (p.place === "into") {
      const group = blocksTree(handle.doc).getNodeByID(p.target.treeId);
      const count = group?.children()?.length ?? 0;
      const isCollapsed = collapsed.has(p.target.treeId);
      moveBlock(handle.doc, active, p.target.treeId, altHeld.current || isCollapsed ? count : 0);
      if (isCollapsed) toggleCollapse(p.target.treeId);
    } else {
      moveBlockRelative(handle.doc, active, p.target.treeId, p.place);
    }
  };
  const moveIntoGroupAbove = (row: FlatBlock) => {
    const prev = sibling(row, -1);
    if (!prev || prev.type !== "group") return undefined;
    return () => moveBlock(handle.doc, row.treeId, prev.treeId, blocksTree(handle.doc).getNodeByID(prev.treeId)?.children()?.length ?? 0);
  };
  const moveOutOfGroup = (row: FlatBlock) => (row.parent && !(rootTreeId && row.treeId === rootTreeId) && allRows.some((r) => r.treeId === row.parent) ? () => moveBlockRelative(handle.doc, row.treeId, row.parent!, "after") : undefined);

  const sibling = (row: FlatBlock, dir: -1 | 1): FlatBlock | undefined => {
    const sibs = allRows.filter((r) => r.parent === row.parent);
    const i = sibs.findIndex((r) => r.treeId === row.treeId);
    return sibs[i + dir];
  };

  const add = (type: BlockType, after?: FlatBlock) => {
    // adding "after" an expanded group inserts as its first child (spec §7.4.1 grouping)
    const intoGroup = after?.type === "group" && !collapsed.has(after.treeId);
    const id = intoGroup
      ? createBlock(handle.doc, type, userId, after!.treeId, 0)
      : createBlock(handle.doc, type, userId, after?.parent ?? undefined, after ? after.index + 1 : undefined);
    setTimeout(() => ctx.focusBlock(id, "start"), 30);
  };

  const renderRow = (row: FlatBlock, index: number): ReactNode => {
    const node = blocksTree(handle.doc).getNodeByID(row.treeId);
    if (!node) return null;
    const Def = blockDef(row.type);
    const selectedIds = selection.has(row.treeId) ? selection : new Set([row.treeId]);
    const n = selectedIds.size;
    // the mirrored block itself: inserting beside it or moving it would act outside the mirror
    const mirrorRoot = !!rootTreeId && index === 0;
    // [M21] no grip for a reader who cannot write, nor inside a mirror: the height belongs to the
    // page that owns the block, not to every page showing it (§8.6 item 8)
    const canResize = !readOnly && !embedded;
    const onDelete = () => {
      if (embedded && !window.confirm(t("block.mirror.confirmDelete", { source: embedded.sourceLabel }))) return;
      deleteBlock(handle.doc, row.treeId);
    };
    return (
      <>
        <SortableRow id={row.treeId}>
          {(dragProps, setRef, style) => (
            <BlockChrome
              depth={row.depth}
              readOnly={readOnly}
              focusedBy={focusedBy.get(row.treeId)}
              workspaceId={workspaceId}
              issues={issues?.get(row.treeId)}
              roles={roleIris.get(row.treeId)}
              dragHandleProps={dragProps}
              setDragRef={setRef}
              style={style}
              selected={selection.has(row.treeId)}
              dropHint={dropHint?.treeId === row.treeId ? dropHint.place : undefined}
              onHandleClick={(e) => selectRow(row, e)}
              height={heights.get(row.treeId) ?? null}
              resize={Def.resize}
              onResize={canResize ? (px) => setBlockHeight(handle.doc, node, px) : undefined}
              onResetHeight={canResize ? () => clearBlockHeight(handle.doc, node) : undefined}
            >
              <div data-block-id={row.treeId} data-block-type={row.type}>
                <Def.Component
                  handle={handle}
                  treeId={row.treeId}
                  node={node}
                  readOnly={readOnly}
                  userId={userId}
                  workspaceId={workspaceId}
                  onFocus={() => onFocusBlock(row.treeId)}
                  collapsed={collapsed.has(row.treeId)}
                  onToggleCollapse={() => toggleCollapse(row.treeId)}
                  onExitUp={() => index > 0 && ctx.focusBlock(rows[index - 1]!.treeId, "end")}
                  onExitDown={() => index < rows.length - 1 && ctx.focusBlock(rows[index + 1]!.treeId, "start")}
                />
              </div>
            </BlockChrome>
          )}
        </SortableRow>
        {/* the row of round controls on the block's bottom edge (spec §8.6 item 5): ＋ and ☰ */}
        <div className="relative h-4 opacity-0 transition focus-within:opacity-100 group-hover/row:opacity-100 has-[[data-menu-open]]:opacity-100">
          {!readOnly && !mirrorRoot && <AddBlockMenu onAdd={(t) => add(t, row)} />}
          <BlockMenu
            className={readOnly || mirrorRoot ? "left-8" : "left-14"}
            iri={blockIri(handle.crdtDocId, row.treeId)}
            readOnly={readOnly}
            onDelete={onDelete}
            onMoveUp={sibling(row, -1) && (() => moveBlockRelative(handle.doc, row.treeId, sibling(row, -1)!.treeId, "before"))}
            onMoveDown={sibling(row, 1) && (() => moveBlockRelative(handle.doc, row.treeId, sibling(row, 1)!.treeId, "after"))}
            onMoveIntoGroup={mirrorRoot ? undefined : moveIntoGroupAbove(row)}
            onMoveOutOfGroup={mirrorRoot ? undefined : moveOutOfGroup(row)}
            onLink={onLinkBlock && (() => onLinkBlock(row.treeId, row.type))}
            onSetRole={onSetRole && (() => onSetRole(row.treeId))}
            sequences={sequences}
            onAddToSequence={onAddToSequence && ((seqId: string) => onAddToSequence(seqId, row.treeId))}
            copyLabel={n > 1 ? t("block.menu.copyN", { count: n }) : t("block.menu.copy")}
            onCopy={workspaceId ? () => void copy(selectedIds) : undefined}
            onPasteAfter={canPaste && !mirrorRoot ? () => openPaste({ parent: row.parent ?? undefined, index: row.index + 1 }, "paste.title.afterBlock") : undefined}
            onPasteInto={canPaste && row.type === "group" ? () => openPaste({ parent: row.treeId }, "paste.title.intoGroup") : undefined}
            mirror={row.type === "mirror" ? { onDetach: readOnly ? undefined : () => void detach(row), original: workspaceId ? originalOf(node, workspaceId) : undefined } : undefined}
          />
        </div>
      </>
    );
  };

  const toast = copied && (
    <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded bg-neutral-800 px-3 py-1 text-sm text-white shadow" role="status" data-testid="copy-toast">
      {copied}
    </div>
  );
  const dialog = pasting && (
    <PasteDialog
      entry={pasting.entry}
      handle={handle}
      target={pasting.target}
      userId={userId}
      where={pasting.where}
      onClose={() => setPasting(null)}
      onPasted={(roots) => {
        setSelection(new Set());
        if (roots[0]) setTimeout(() => ctx.focusBlock(roots[0]!, "start"), 30);
      }}
    />
  );

  if (embedded) {
    return (
      <div className="px-1" data-testid="mirror-tree">
        <DndContext sensors={sensors} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={() => setDropHint(null)}>
          <SortableContext items={ids} strategy={stillRows}>
            {rows.map((row, i) => (
              <div key={row.treeId} className="group/row relative has-[[data-menu-open]]:z-30">
                {renderRow(row, i)}
              </div>
            ))}
          </SortableContext>
        </DndContext>
        {rows.length === 0 && <div className="p-2 text-xs text-neutral-500">{t("block.none")}</div>}
        {dialog}
        {toast}
      </div>
    );
  }

  return (
    <div ref={parentRef} className="h-full overflow-auto px-2 pb-32" onClick={(e) => e.target === e.currentTarget && selection.size && setSelection(new Set())}>
      <DndContext sensors={sensors} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={() => setDropHint(null)}>
        <SortableContext items={ids} strategy={stillRows}>
          <div
            style={
              railLayout && (railLayout.leftGutter || railLayout.rightGutter)
                ? { height: virtualizer.getTotalSize(), position: "relative", marginLeft: railLayout.leftGutter, marginRight: railLayout.rightGutter }
                : { height: virtualizer.getTotalSize(), position: "relative" }
            }
          >
            {railLayout && rail && <RailOverlay layout={railLayout} height={virtualizer.getTotalSize()} endpoints={rail.endpoints} onJump={jump} />}
            {virtualizer.getVirtualItems().map((vi) => {
              const row = rows[vi.index]!;
              // each row is absolutely positioned with a transform, i.e. its own stacking context: a
              // menu's z-index cannot lift it above later rows, so the row owning an open menu is raised
              return (
                <div key={row.treeId} data-index={vi.index} ref={virtualizer.measureElement} className="group/row has-[[data-menu-open]]:z-30" style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${vi.start}px)` }}>
                  {renderRow(row, vi.index)}
                </div>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
      {!readOnly && rows.length === 0 && (
        <div className="p-4 text-sm text-neutral-500">
          {t("block.emptyPage")} <AddBlockMenu onAdd={(type) => add(type)} always label={t("block.add")} />
          {canPaste && <PasteButton onClick={() => openPaste({}, "paste.title.atEnd")} />}
        </div>
      )}
      {!readOnly && rows.length > 0 && (
        // appends at the end of the page; the per-block inserters above only appear on hover
        <div className="flex gap-2 px-8 py-3">
          <AddBlockMenu onAdd={(type) => add(type)} always label={t("block.add")} />
          {canPaste && <PasteButton onClick={() => openPaste({}, "paste.title.atEnd")} />}
        </div>
      )}
      {dialog}
      {toast}
    </div>
  );
}

/** Rows keep their places while a block is dragged; the drop indicator shows where it will land. */
const stillRows: SortingStrategy = () => null;

function originalOf(node: { data: { get(k: string): unknown } }, workspaceId: string): { workspaceId: string; docId: string; treeId?: string } | undefined {
  const body = node.data.get("body") as { get(k: string): unknown } | undefined;
  const source = body?.get?.("source") as { kind: string; iri: string } | null | undefined;
  const m = source && /^urn:km:(?:blk|doc):([0-9a-f-]{36})(?::(\d+@\d+))?$/.exec(source.iri);
  return m ? { workspaceId, docId: m[1]!, treeId: m[2] } : undefined;
}

function PasteButton({ onClick }: { onClick(): void }) {
  const t = useT();
  return (
    <button className="ml-2 inline-flex items-center gap-1 rounded border border-neutral-300 px-2 py-1 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800" onClick={onClick} data-testid="paste-end">
      <ClipboardPaste size={14} /> {t("block.paste")}
    </button>
  );
}

function SortableRow({ id, children }: { id: string; children: (dragProps: Record<string, unknown>, setRef: (el: HTMLElement | null) => void, style: React.CSSProperties) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = { transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return <>{children({ ...attributes, ...listeners }, setNodeRef, style)}</>;
}

function AddBlockMenu({ onAdd, always, label }: { onAdd: (t: BlockType) => void; always?: boolean; label?: string }) {
  // dismissal is the shared one of §8.6 item 5, so this menu and the block's ☰ are never both up
  const { open, toggle, close, buttonRef, panelRef } = usePopover();
  const t = useT();
  return (
    <div
      // the per-block ＋ is placed by the control row under its block, which handles its visibility
      className={always ? "inline-block" : "contents"}
    >
      <button
        className={
          always
            ? "inline-flex items-center gap-1 rounded border border-neutral-300 px-2 py-1 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
            : "absolute -top-2 left-8 rounded-full border border-neutral-300 bg-white p-0.5 text-neutral-500 hover:text-neutral-900 dark:bg-neutral-900"
        }
        ref={buttonRef}
        onClick={toggle}
        aria-label={t("block.add")}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={always ? "add-block-always" : "add-block-hover"}
      >
        <Plus size={14} />
        {label && always ? <span>{label}</span> : null}
      </button>
      {open && (
        <div ref={panelRef} className="absolute left-8 top-2 z-10 w-56 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700" data-menu-open="" data-testid="add-block-menu">
          {(Object.keys(blockRegistry) as BlockType[]).filter((type) => type !== "mirror").map((type) => {
            const d = blockDef(type);
            const enabled = availableBlockTypes.includes(type);
            return (
              <button key={type} disabled={!enabled} className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800" onClick={() => { close(); onAdd(type); }}>
                <d.icon size={14} /> {t(d.labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function sameRows(a: FlatBlock[], b: FlatBlock[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!, y = b[i]!;
    if (x.treeId !== y.treeId || x.depth !== y.depth || x.type !== y.type || x.parent !== y.parent || x.index !== y.index) return false;
  }
  return true;
}
